import React, { useState, useEffect } from 'react';
import './Admin.css';
import Icon from './Icon';
import { isDevMode } from './dev';
import { formatPhone } from './phone';

function fmtTime(iso) { return iso ? new Date(iso).toLocaleString() : ''; }

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString() : ''; }

function Admin({ accounts, currentUsername, orgName, mlsName = '', seats, usedSeats, onAddUser, onUpdateUser, onDeleteUsers, onSetDisabled, onResetPassword, onImpersonate, onSetExpiry, onSetPaid, hideAdminGrant, canImpersonate = false, heading = 'Users', addLabel = 'Add User' }) {
  const seatsFull = typeof seats === 'number' && typeof usedSeats === 'number' && usedSeats >= seats;
  const overCapacity = typeof seats === 'number' && typeof usedSeats === 'number' && usedSeats > seats;
  const [showAdd, setShowAdd] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [email, setEmail]       = useState('');
  const [isAdmin, setIsAdmin]   = useState(false);
  const [phone, setPhone]       = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [mlsBrokerId, setMlsBrokerId] = useState('');
  const defaultInviteSubject = orgName ? `Set up your ${orgName} account on iBetterAgent` : 'Set up your iBetterAgent account';
  const defaultInvite = `Welcome${orgName ? ' to ' + orgName : ''}! An account has been created for you. Use the secure link below to set your password and sign in.`;
  const [inviteSubject, setInviteSubject] = useState(defaultInviteSubject);
  const [inviteMessage, setInviteMessage] = useState(defaultInvite);
  const [detailsFor, setDetailsFor] = useState(null);   // username (email) whose edit card is expanded
  const [editDraft, setEditDraft] = useState({});
  const [error, setError]       = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);   // string[] | null
  const [historyFor, setHistoryFor] = useState(null);          // username | null
  const [notice, setNotice]     = useState('');
  const [impFor, setImpFor]     = useState(null);              // username being impersonated | null
  const [impNote, setImpNote]   = useState('');
  const [search, setSearch]     = useState('');

  const q = search.toLowerCase().trim();
  const shown = q
    ? accounts.filter(a =>
        (a.username || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.alias || '').toLowerCase().includes(q))
    : accounts;

  async function add() {
    const err = await onAddUser({ alias: agentName, email, phone, licenseId, mlsBrokerId, admin: isAdmin, inviteSubject, inviteMessage });
    if (err) { setError(err); return; }
    setNotice(`Agent "${(agentName.trim() || email.trim())}" added — the invite was emailed to ${email.trim()}.`);
    setAgentName(''); setEmail(''); setIsAdmin(false);
    setPhone(''); setLicenseId(''); setMlsBrokerId('');
    setInviteSubject(defaultInviteSubject); setInviteMessage(defaultInvite);
    setShowAdd(false); setError('');
  }
  function doDelete() {
    onDeleteUsers(confirmDelete);
    setNotice(`Deleted ${confirmDelete.length} user(s).`);
    setConfirmDelete(null);
  }
  function openEdit(a) {
    if (detailsFor === a.username) { setDetailsFor(null); return; }
    setDetailsFor(a.username);
    setEditDraft({ alias: a.alias || '', email: a.email || '', phone: a.phone || '', licenseId: a.licenseId || '', mlsBrokerId: a.mlsBrokerId || '' });
  }
  async function saveEdit(a) {
    const r = await onUpdateUser(a.id, { alias: editDraft.alias, email: editDraft.email, phone: editDraft.phone, licenseId: editDraft.licenseId, mlsBrokerId: editDraft.mlsBrokerId });
    if (r?.error) { setNotice(`⚠ ${r.error}`); return; }
    setNotice(r?.inviteResent ? `Updated "${a.username}" — a new setup link was emailed to the updated address.` : `Updated "${a.username}".`);
    setDetailsFor(null);
  }
  async function resetPw(acct) {
    if (!acct.email) { setNotice(`⚠ "${acct.username}" has no email on file — add one first.`); return; }
    setNotice('Sending reset link…');
    const r = await onResetPassword(acct.id);
    if (r?.error) { setNotice(`⚠ ${r.error}`); return; }
    setNotice(r?.sent ? `Reset link sent to ${acct.email}.` : `Reset link generated for "${acct.username}".`);
  }
  function startImpersonate(u) { setImpFor(u); setImpNote(''); setNotice(''); }
  async function confirmImpersonate() {
    const err = await onImpersonate(impFor, impNote);
    if (err) { setNotice(`⚠ ${err}`); return; }
    // On success the app switches to the impersonated user and this view unmounts.
    setImpFor(null); setImpNote('');
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>{heading}{orgName ? ` — ${orgName}` : ''}</h2>
          <p>Manage user accounts — add, enable/disable, or delete.
            {typeof seats === 'number' && <> {' '}<strong className={seatsFull ? 'admin-seats-full' : 'admin-seats'}>{usedSeats} / {seats} seats used</strong>.</>}
          </p>
        </div>
        <div className="admin-header-btns">
          <button className="admin-add-btn" onClick={() => { setShowAdd(s => !s); setError(''); }}><Icon name="add" size={16} /> {addLabel}</button>
        </div>
      </div>

      {overCapacity && (
        <div className="admin-overcap-banner">
          ⛔ <strong>Over capacity</strong> — {usedSeats} active members but only {seats} seat{seats === 1 ? '' : 's'}. Reminder sending is <strong>paused org-wide</strong>. Disable {usedSeats - seats} member{usedSeats - seats === 1 ? '' : 's'} below (their data is kept), or contact iBetterAgent at <a href="mailto:support@ibetteragent.com">support@ibetteragent.com</a> to purchase more seats, to resume.
        </div>
      )}

      {notice && <div className="admin-notice">{notice} <button className="admin-notice-x" onClick={() => setNotice('')}>✕</button></div>}

      {impFor && (
        <div className="admin-imp-panel">
          <div className="admin-imp-title"><Icon name="shield-check" size={18} /> Impersonate "{impFor}" for support</div>
          <p className="admin-imp-hint">You'll be signed in as this user to provide support. Enter a reason — it's logged and visible to the user.</p>
          <textarea className="admin-imp-note" rows={2} autoFocus
            placeholder="Reason for accessing this account (required)…"
            value={impNote} onChange={e => setImpNote(e.target.value)} />
          <div className="admin-imp-actions">
            <button className="rt-save-btn" onClick={confirmImpersonate} disabled={!impNote.trim()}>Sign in as Agent</button>
            <button className="contacts-import-cancel-btn" onClick={() => { setImpFor(null); setImpNote(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="admin-add-panel">
          <div className="admin-add-title">New User</div>
          {error && <div className="admin-error">⚠ {error}</div>}
          <div className="admin-add-grid">
            <div className="admin-field">
              <label className="admin-label">Agent name <span className="detail-required">*</span></label>
              <input className="admin-input" value={agentName} onChange={e => { setAgentName(e.target.value); setError(''); }} placeholder="Full name" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Email <span className="detail-required">*</span> <span className="admin-hint-inline">— this is the login</span></label>
              <input type="email" className="admin-input" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="agent@example.com" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Phone</label>
              <input type="tel" className="admin-input" placeholder="XXX-XXX-XXXX" maxLength={12} value={phone} onChange={e => { setPhone(formatPhone(e.target.value)); setError(''); }} />
            </div>
            <div className="admin-field">
              <label className="admin-label">License ID</label>
              <input className="admin-input" value={licenseId} onChange={e => { setLicenseId(e.target.value); setError(''); }} />
            </div>
            <div className="admin-field">
              <label className="admin-label">MLS Broker ID</label>
              <input className="admin-input" value={mlsBrokerId} onChange={e => { setMlsBrokerId(e.target.value); setError(''); }} />
            </div>
          </div>
          <p className="admin-hint">No password needed — the agent gets the invite below with a secure link to set their password and sign in. Edit the message before sending if you like.</p>
          <div className="admin-field">
            <label className="admin-label">Invite subject</label>
            <input className="admin-input" value={inviteSubject} onChange={e => { setInviteSubject(e.target.value); setError(''); }} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Invite message <span className="admin-hint">(the secure “Set my password” link is added automatically)</span></label>
            <textarea className="admin-input" rows={4} value={inviteMessage} onChange={e => { setInviteMessage(e.target.value); setError(''); }} />
          </div>
          {!hideAdminGrant && (
            <label className="admin-checkbox">
              <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
              Grant admin privileges
            </label>
          )}
          <div className="admin-add-actions">
            <button className="rt-save-btn" onClick={add}>Create User</button>
            <button className="contacts-import-cancel-btn" onClick={() => { setShowAdd(false); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="contacts-delete-notice">
          <span><Icon name="delete" size={16} /> Delete <strong>{confirmDelete.length}</strong> user(s)? This cannot be undone. (Admin accounts are protected.)</span>
          <button className="contacts-delete-confirm-btn" onClick={doDelete}>Delete</button>
          <button className="contacts-import-cancel-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
        </div>
      )}

      <div className="admin-toolbar">
        <input className="admin-search" type="text"
          placeholder="🔍 Filter by username, email, or alias…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {q && <span className="admin-search-count">{shown.length} of {accounts.length}</span>}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Alias</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              {onSetExpiry && <th>Trial</th>}
              {onSetPaid && <th>Subscription</th>}
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={6 + (onSetExpiry ? 1 : 0) + (onSetPaid ? 1 : 0)} className="admin-no-results">No agents match “{search}”.</td></tr>
            )}
            {shown.map(a => {
              const isSelf = a.username === currentUsername;
              const history = a.history || [];
              return (
                <React.Fragment key={a.username}>
                  <tr>
                    <td className="admin-username" data-label="Username">{a.username}{isSelf && <span className="admin-you">you</span>}</td>
                    <td data-label="Alias">{a.alias || '—'}</td>
                    <td className="admin-email" data-label="Email">{a.email || '—'}</td>
                    <td data-label="Role"><span className={`admin-role-badge ${a.admin ? 'admin' : 'user'}`}>{a.admin ? 'Admin' : 'User'}</span></td>
                    <td data-label="Status">
                      {a.disabled
                        ? <span className="admin-status-badge disabled">Disabled</span>
                        : a.mustChangePassword
                          ? <span className="admin-status-badge pending" title="Setup link sent — hasn't signed in yet">Invited</span>
                          : <span className="admin-status-badge active">Active</span>}
                    </td>
                    {onSetExpiry && (
                      <td className="admin-expires" data-label="Trial">
                        <input type="date" className="admin-exp-input"
                          value={a.expiresAt ? a.expiresAt.slice(0, 10) : ''}
                          onChange={e => onSetExpiry(a.username, e.target.value ? new Date(e.target.value + 'T12:00:00').toISOString() : null)} />
                        {a.expiresAt
                          ? (new Date(a.expiresAt) < new Date()
                              ? <span className="admin-exp-badge exp">expired {fmtDate(a.expiresAt)}</span>
                              : <span className="admin-exp-badge">until {fmtDate(a.expiresAt)}</span>)
                          : <span className="admin-exp-badge none">no expiry</span>}
                      </td>
                    )}
                    {onSetPaid && (
                      <td className="admin-expires" data-label="Subscription">
                        <input type="date" className="admin-exp-input" title="Paid-subscription end date — set after the agent pays"
                          value={a.paidUntil ? a.paidUntil.slice(0, 10) : ''}
                          onChange={e => onSetPaid(a.username, e.target.value ? new Date(e.target.value + 'T12:00:00').toISOString() : null)} />
                        {a.paidUntil
                          ? (new Date(a.paidUntil) < new Date()
                              ? <span className="admin-exp-badge exp">lapsed {fmtDate(a.paidUntil)}</span>
                              : <span className="admin-exp-badge paid">paid · until {fmtDate(a.paidUntil)}</span>)
                          : <span className="admin-exp-badge none">not paid</span>}
                      </td>
                    )}
                    <td className="admin-actions">
                      <button className="admin-toggle-btn" disabled={isSelf}
                        title={isSelf ? "You can't disable yourself" : ''}
                        onClick={() => { const err = onSetDisabled([a.username], !a.disabled); setNotice(err ? `⚠ ${err}` : `${a.disabled ? 'Enabled' : 'Disabled'} "${a.username}".`); }}>
                        {a.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button className="admin-reset-btn" onClick={() => resetPw(a)}>Reset</button>
                      {/* Impersonate is a System-operator support tool only (not gated by Developer mode). */}
                      {canImpersonate && (
                        <button className={`admin-imp-btn ${isDevMode() ? 'dev-tool' : ''}`} disabled={isSelf || a.admin}
                          title={isSelf ? "That's you" : a.admin ? "Can't impersonate another admin" : 'Sign in as this user for support'}
                          onClick={() => startImpersonate(a.username)}>Impersonate</button>
                      )}
                      <button className="admin-hist-btn" onClick={() => openEdit(a)}>Edit</button>
                      <button className="admin-hist-btn" disabled={!history.length}
                        onClick={() => setHistoryFor(historyFor === a.username ? null : a.username)}>
                        History{history.length ? ` (${history.length})` : ''}
                      </button>
                      <button className="admin-del-btn" disabled={a.admin}
                        title={a.admin ? "Admin accounts can't be deleted" : 'Delete user'}
                        onClick={() => setConfirmDelete([a.username])}>Delete</button>
                    </td>
                  </tr>
                  {detailsFor === a.username && (
                    <tr className="admin-history-row">
                      <td colSpan={onSetExpiry ? 7 : 6}>
                        <div className="admin-agent-card">
                          <div className="admin-agent-card-title">Edit {a.username}</div>
                          <div className="admin-agent-card-grid">
                            <div><span className="admin-card-k">Agent name</span><input className="admin-input" value={editDraft.alias || ''} onChange={e => setEditDraft(d => ({ ...d, alias: e.target.value }))} /></div>
                            <div><span className="admin-card-k">Email</span><input className="admin-input" type="email" value={editDraft.email || ''} onChange={e => setEditDraft(d => ({ ...d, email: e.target.value }))} /></div>
                            <div><span className="admin-card-k">Phone</span><input className="admin-input" placeholder="XXX-XXX-XXXX" maxLength={12} value={editDraft.phone || ''} onChange={e => setEditDraft(d => ({ ...d, phone: formatPhone(e.target.value) }))} /></div>
                            <div><span className="admin-card-k">License ID</span><input className="admin-input" value={editDraft.licenseId || ''} onChange={e => setEditDraft(d => ({ ...d, licenseId: e.target.value }))} /></div>
                            <div><span className="admin-card-k">MLS Broker ID</span><input className="admin-input" value={editDraft.mlsBrokerId || ''} onChange={e => setEditDraft(d => ({ ...d, mlsBrokerId: e.target.value }))} /></div>
                            <div><span className="admin-card-k">Login status</span>{a.disabled ? 'Disabled' : a.mustChangePassword ? 'Invited — not signed in yet' : 'Active — signed in'}</div>
                          </div>
                          <div className="admin-add-actions">
                            <button className="rt-save-btn" onClick={() => saveEdit(a)}>Save changes</button>
                            <button className="contacts-import-cancel-btn" onClick={() => setDetailsFor(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {historyFor === a.username && (
                    <tr className="admin-history-row">
                      <td colSpan={onSetExpiry ? 7 : 6}>
                        <div className="admin-history">
                          <div className="admin-history-title">Enable / Disable history — {a.username}</div>
                          {history.length ? (
                            <ul className="admin-history-list">
                              {[...history].reverse().map((h, i) => (
                                <li key={i}>
                                  <span className={`admin-history-action ${h.action}`}>{h.action === 'disabled' ? '🚫 Disabled' : '✅ Enabled'}</span>
                                  <span className="admin-history-time">{fmtTime(h.at)}</span>
                                  <span className="admin-history-by">by {h.by}</span>
                                </li>
                              ))}
                            </ul>
                          ) : <div className="admin-history-empty">No actions recorded.</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Admin;
